import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AnimatePresence, motion } from "framer-motion";
import UserProfileButton from "./UserProfileButton";
import NotificationBell from "./NotificationBell";
import { Button } from "@/components/ui/button";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 10);
    };
    
    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial scroll position
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const isHomepage = location.pathname === "/";
  
  const scrollToSection = (sectionId: string) => {
    if (!isHomepage) {
      return true;
    }
    
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
      setIsMenuOpen(false);
      return false;
    }
    return true;
  };
  
  const publicNavItems = [
    { name: "Problem", path: "/#problem", sectionId: "problem" },
    { name: "Solution", path: "/#solution", sectionId: "solution" },
    { name: "How It Works", path: "/#how-it-works", sectionId: "how-it-works" },
    { name: "Benefits", path: "/#benefits", sectionId: "benefits" },
    { name: "Use Cases", path: "/#use-cases", sectionId: "use-cases" },
  ];
  
  const privateNavItems = [
    { name: "Dashboard", path: "/dashboard", sectionId: null },
    { name: "Inventory", path: "/inventory", sectionId: null },
    { name: "Luna Chat", path: "/luna-chat", sectionId: null },
  ];
  
  const rightItems = [
    { name: "Pricing", path: "/pricing", sectionId: null },
  ];

  const navItems = user ? privateNavItems : publicNavItems;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHomepage
          ? "bg-white/90 backdrop-blur-md border-b border-gray-200/80 py-3 shadow-sm"
          : "bg-transparent py-4"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center text-gray-900 font-bold text-xl">
            <span className="text-expitrack-primary">Expi</span>Track
          </Link>

          <nav className="hidden md:flex items-center justify-center flex-1">
            <div className="flex items-center space-x-2 px-2">
              {navItems.map((item) => (
                item.sectionId ? (
                  <a
                    key={item.name}
                    href={item.path}
                    onClick={(e) => {
                      const allowDefault = scrollToSection(item.sectionId!);
                      if (!allowDefault) {
                        e.preventDefault();
                      }
                    }}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors 
                      text-gray-600 hover:text-expitrack-primary hover:bg-expitrack-primary/5
                    `}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname === item.path
                        ? "text-expitrack-primary bg-expitrack-primary/5"
                        : "text-gray-600 hover:text-expitrack-primary hover:bg-expitrack-primary/5"
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>
          </nav>

          <div className="flex items-center space-x-2">
            <div className="hidden md:flex items-center">
              {!user && rightItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    location.pathname === item.path
                      ? "text-expitrack-primary bg-expitrack-primary/5"
                      : "text-gray-600 hover:text-expitrack-primary hover:bg-expitrack-primary/5"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
            
            {user ? (
              <div className="flex items-center space-x-2">
                <NotificationBell />
                <UserProfileButton />
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to="/auth">
                  <Button
                    variant="secondary"
                    className="hidden md:inline-flex"
                    size="sm"
                  >
                    Sign In
                  </Button>
                </Link>
                <Link to="/auth" state={{ isSignUp: true }}>
                  <Button
                    className="hidden md:inline-flex bg-expitrack-primary hover:bg-expitrack-primary/90"
                    size="sm"
                  >
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}
            
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              {isMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>
      
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-white border-b border-gray-200"
          >
            <div className="container mx-auto px-4 py-3 space-y-1">
              {navItems.map((item) => (
                item.sectionId ? (
                  <a
                    key={item.name}
                    href={item.path}
                    onClick={(e) => {
                      const allowDefault = scrollToSection(item.sectionId!);
                      if (!allowDefault) {
                        e.preventDefault();
                      }
                    }}
                    className="block px-3 py-2 text-base font-medium rounded-md text-gray-600 hover:text-expitrack-primary hover:bg-expitrack-primary/5"
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    className="block px-3 py-2 text-base font-medium rounded-md text-gray-600 hover:text-expitrack-primary hover:bg-expitrack-primary/5"
                  >
                    {item.name}
                  </Link>
                )
              ))}
              {!user && rightItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="block px-3 py-2 text-base font-medium rounded-md text-gray-600 hover:text-expitrack-primary hover:bg-expitrack-primary/5"
                >
                  {item.name}
                </Link>
              ))}
              {!user && (
                <div className="pt-4 flex flex-col space-y-2">
                  <Link to="/auth" className="w-full">
                    <Button variant="outline" className="w-full">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/auth" state={{ isSignUp: true }} className="w-full">
                    <Button className="w-full bg-expitrack-primary hover:bg-expitrack-primary/90">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
